import { useEffect, useState } from 'react';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';

export interface IdentitySummary {
  displayName: string;
  initials: string;
  /** Undefined only while the identity API is still resolving, or if the
   * entity ref is not in the expected `user:<ns>/<local-part>` shape. */
  email?: string;
}

/** Reverses the namespace mapping in the sign-in resolver
 * (packages/backend/src/auth/google-domain-provider.ts): `default` is
 * mumzworld.com, other allowed domains get `<domain with dots as dashes>`.
 * Only a fallback — the real profile email is preferred below. Keep in sync
 * with ALLOWED_EMAIL_DOMAINS there. Unknown namespace -> undefined, so an
 * unexpected ref never maps onto someone else's address. */
const NAMESPACE_TO_EMAIL_DOMAIN: Record<string, string> = {
  default: 'mumzworld.com',
  'andador-me': 'andador.me',
};

function emailFromEntityRef(userEntityRef: string): string | undefined {
  // `user:default/baharul.khan`  -> `baharul.khan@mumzworld.com`
  // `user:andador-me/john`       -> `john@andador.me`
  const match = /^user:([^/]+)\/(.+)$/.exec(userEntityRef);
  if (!match) return undefined;
  const domain = NAMESPACE_TO_EMAIL_DOMAIN[match[1].toLowerCase()];
  if (!domain) return undefined;
  return `${match[2].toLowerCase()}@${domain}`;
}

function initialsFor(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Real signed-in identity (guest auth today) — not a hardcoded name. Falls
 * back to "Guest" only while the identity API is still resolving. */
export function useIdentity(): IdentitySummary {
  const identityApi = useApi(identityApiRef);
  const [identity, setIdentity] = useState<{ displayName: string; email?: string }>({
    displayName: 'Guest',
  });

  // Both lookups are resolved together and committed in a SINGLE state update:
  // two independent setStates here meant two extra renders for every consumer,
  // and `allSettled` (not `all`) so a failing profile lookup doesn't also
  // discard a perfectly good entity ref, or vice versa.
  useEffect(() => {
    let cancelled = false;

    Promise.allSettled([identityApi.getProfileInfo(), identityApi.getBackstageIdentity()]).then(
      ([profileResult, identityResult]) => {
        if (cancelled) return;

        const profile = profileResult.status === 'fulfilled' ? profileResult.value : undefined;
        const backstageIdentity = identityResult.status === 'fulfilled' ? identityResult.value : undefined;

        setIdentity({
          displayName: profile?.displayName || 'Guest',
          // Prefer the real email when the profile carries one, and only fall
          // back to deriving it from the entity ref when it doesn't.
          email:
            profile?.email?.toLowerCase() ??
            (backstageIdentity ? emailFromEntityRef(backstageIdentity.userEntityRef) : undefined),
        });
      },
    );

    return () => {
      cancelled = true;
    };
  }, [identityApi]);

  return {
    displayName: identity.displayName,
    initials: initialsFor(identity.displayName),
    email: identity.email,
  };
}
